import { useState } from "react";
import { games } from "@/mocks/games";

const RESY_BASE = "https://resy.com/cities/los-angeles-ca/venues/n17-the-lane";

const groupFilters = ["All", "Group Stage", "Round of 32", "Round of 16", "Quarterfinal", "Semifinal", "Third Place", "FINAL"];

function getStage(group: string) {
  if (group.startsWith("Group")) return "Group Stage";
  return group;
}

export default function GameSchedule() {
  const [filter, setFilter] = useState("All");

  const filtered = filter === "All" ? games : games.filter((g) => getStage(g.group) === filter);

  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr + "T12:00:00");
    return d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", year: "numeric" });
  };

  return (
    <section id="schedule" className="bg-[#0a0a0a] py-24 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-12 gap-4">
          <div>
            <span className="text-[#e8c34a] text-xs uppercase tracking-[0.3em] font-semibold block mb-3">
              All 104 Matches
            </span>
            <h2 className="text-white font-black text-4xl md:text-6xl uppercase leading-none">
              Game Schedule
              <br />
              <span className="text-[#e8c34a]">&amp; Reservations</span>
            </h2>
          </div>
          <p className="text-white/50 text-sm max-w-sm leading-relaxed">
            Every single FIFA World Cup 2026 fixture. Each &quot;Book via Resy&quot; button is pre-linked to that exact game date at{" "}
            <span className="text-[#e8c34a] font-medium">N17 The Lane</span>. All times Pacific (PST).
          </p>
        </div>

        {/* Filter tabs */}
        <div className="flex gap-2 flex-wrap mb-10">
          {groupFilters.map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-4 py-2 rounded-full text-xs uppercase tracking-wider font-bold transition-all cursor-pointer whitespace-nowrap ${
                filter === f
                  ? "bg-[#e8c34a] text-black"
                  : "bg-white/5 border border-white/10 text-white/50 hover:bg-white/10 hover:text-white"
              }`}
            >
              {f}
            </button>
          ))}
        </div>

        {/* Count */}
        <p className="text-white/30 text-xs uppercase tracking-widest mb-6">
          Showing {filtered.length} match{filtered.length !== 1 ? "es" : ""}
        </p>

        {/* Games grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {filtered.map((game) => (
            <div
              key={game.id}
              className={`relative rounded-2xl border transition-all hover:border-[#e8c34a]/40 ${
                game.featured
                  ? "bg-gradient-to-br from-[#1a1500] to-[#0a0a0a] border-[#e8c34a]/30"
                  : "bg-white/5 border-white/10"
              }`}
            >
              {game.featured && (
                <span className="absolute top-4 left-4 bg-[#e8c34a] text-black text-[10px] font-black uppercase tracking-wider px-3 py-1 rounded-full">
                  Featured
                </span>
              )}

              <div className="p-6">
                <div className="flex items-center justify-between mb-5 mt-1">
                  <span className="text-white/30 text-[11px] uppercase tracking-widest font-medium">
                    {game.group}
                  </span>
                  <span className="text-white/30 text-[11px] uppercase tracking-widest font-medium">
                    {game.time}
                  </span>
                </div>

                {/* Teams */}
                <div className="flex items-center justify-between gap-2 mb-5">
                  <div className="flex flex-col items-center gap-1.5 flex-1">
                    <span className="text-4xl">{game.homeFlag}</span>
                    <span className="text-white font-black text-sm uppercase tracking-wide text-center">
                      {game.homeTeam}
                    </span>
                  </div>
                  <div className="flex flex-col items-center gap-1">
                    <span className="text-white/20 font-black text-2xl">VS</span>
                  </div>
                  <div className="flex flex-col items-center gap-1.5 flex-1">
                    <span className="text-4xl">{game.awayFlag}</span>
                    <span className="text-white font-black text-sm uppercase tracking-wide text-center">
                      {game.awayTeam}
                    </span>
                  </div>
                </div>

                {/* Date & venue */}
                <div className="flex items-center gap-2 mb-1.5">
                  <i className="ri-calendar-line text-[#e8c34a] text-sm w-4 h-4 flex items-center justify-center"></i>
                  <span className="text-white/60 text-xs">{formatDate(game.date)}</span>
                </div>
                <div className="flex items-center gap-2 mb-5">
                  <i className="ri-map-pin-line text-[#e8c34a] text-sm w-4 h-4 flex items-center justify-center"></i>
                  <span className="text-white/60 text-xs">{game.venue}</span>
                </div>

                {/* CTA */}
                <div className="flex gap-2">
                  <a
                    href={game.resyLink}
                    target="_blank"
                    rel="nofollow noopener noreferrer"
                    className="flex-1 flex items-center justify-center gap-2 bg-[#e8c34a] text-black font-black py-3 rounded-xl uppercase tracking-wider text-xs hover:bg-[#f0d060] transition-all cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-reserved-line"></i>
                    Book via Resy
                  </a>
                  <a
                    href="#full90"
                    className="flex items-center justify-center gap-1.5 bg-white/10 border border-white/10 text-white/70 font-bold py-3 px-4 rounded-xl uppercase tracking-wider text-xs hover:bg-white/20 transition-all cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-beer-line text-[#e8c34a]"></i>
                    Full 90
                  </a>
                </div>
              </div>
            </div>
          ))}
        </div>

        <p className="text-center text-white/20 text-xs mt-10 tracking-wide">
          Resy links are pre-set to each game&apos;s date at N17 The Lane. The Full 90 deal available for all 104 matches.
        </p>
      </div>
    </section>
  );
}
